import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { Package , MapPin, Users , DollarSign} from "lucide-react";

import StickyHeader from "../../../../components/ModuleCommon/StickyHeader/StickyHeader";
import { ExpandableHeader } from "../../../../components/ACS-Dashboard/ACS-Blocks/ExpandableHeader";
import { DisplayDetail } from "../../../../components/ACS-Dashboard/ACS-Blocks/DisplayDetail";
import OrderUtil from "../utils/OrderUtil";

const OrderDetailsView = ({ orderNumber, orderOfficeCode }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const headerRef = useRef(null);
  const abortRef = useRef(null);

  const token = Cookies.get("authToken");

  const fetchOrder = useCallback(async () => {
    if (!orderNumber) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/api/orders/order-number/${encodeURIComponent(orderNumber)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Office-Code": orderOfficeCode,
            "Content-Type": "application/json",
          },
          signal: ctrl.signal,
        }
      );
      setOrder(res.data || {});
    } catch (e) {
      if (!axios.isCancel(e))
        setErr(e?.response?.data || e?.message || "Error");
    } finally {
      setLoading(false);
    }
  }, [orderNumber, orderOfficeCode, token]);

  useEffect(() => {
    fetchOrder();
    return () => abortRef.current?.abort();
  }, [fetchOrder]);

      if (loading) {
        return (
          <div className="p-4 flex items-center justify-center h-full w-full">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600" />
          </div>
        );
      }

      if (err) {
        return (
          <div className="p-4 text-red-600">
            Failed to load order: {typeof err === "string" ? err : "Unknown error"}
          </div>
        );
      }

      if (!order) {
        return null;      // 👈 instead of showing “No order found”
      }


  // Prefer DTO fields; fall back to richer entity or XML-like names if present
  const headerInfo = {
    "Order Number": { value: order.orderNumber || "" },
    Origin: {
      value: order.origin || order?.pickup?.location?.locationName || "",
    },
    Destination: {
      value: order.destination || order?.dropoff?.location?.locationName || "",
    },
    Status: { value: order.status || "" },
  };

  const totalWeight = order.weight ?? OrderUtil.sumLineItemWeight(order) ?? null;

  const totalPallets = order.numberOfPallets ?? OrderUtil.sumPallets(order) ?? null;

  const pickupDate =
    order.pickupDate ??
    order?.pickup?.scheduledArrival?.date ??
    order?.pickup?.scheduledArrival?.Date ??
    null;

  const deliveryDate =
    order.deliveryDate ??
    order?.dropoff?.scheduledArrival?.date ??
    order?.dropoff?.scheduledArrival?.Date ??
    null;

  const items = order?.lineItems || order?.items || [];


  const receiver = order?.parties?.find((p) =>
    ["RECEIVER"].includes((p.type || "").toUpperCase())
  );
  const shipper = order?.parties?.find((p) =>
    ["SHIPPER"].includes((p.type || "").toUpperCase())
  );

  return (
     <div className={`w-full h-full min-h-0 flex flex-col transition-opacity duration-500`} >
      {/* Sticky header */}
      <div
        ref={headerRef}
        className="sticky top-0 z-10 bg-white shadow-md w-full"
      >
        <StickyHeader
          infoData={headerInfo}
          buttons={[]}
          mode={null}
          leftLabel="Order Details"
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto  bg-white dark:bg-acs-dark-surface w-full p-fluid-1 scroll-container overscroll-contain text-gray-800 dark:text-acs-dark-text"

>
        {/* Order Details */}
       {/* === Order Details (Static Section) === */}
       <div className="p-fluid-2">
        <h3 className="text-fluid-lg font-semibold mb-fluid-2">Order Details</h3>
         <div className="mb-fluid-3 overflow-hidden  border border-gray-300 dark:border-acs-dark-border  bg-white dark:bg-acs-dark-surface">
          <div className="
              bg-[#1e2a74] dark:bg-blue-900/40 
              text-white dark:acs-dark-text
              text-fluid-base font-medium 
              px-fluid-2 py-fluid-1.5
            ">
            Core Order Details
          </div>
            <div className="grid grid-cols-5 gap-x-fluid-2 gap-y-fluid-3 p-fluid-3 bg-[#F9FAFE] dark:bg-acs-dark-surface text-gray-800 dark:text-acs-dark-text">
            <DisplayDetail label="Order Number" value={order.orderNumber} />
            <DisplayDetail
              label="Office Code"
              value={order.officeCode || orderOfficeCode}
            />
            <DisplayDetail label="PO Number" value={order.poNumber} />
            <DisplayDetail
              label="Requested Mode"
              value={order.requestedMode || order.movementType}
            />
            <DisplayDetail
              label="Created Date"
              value={OrderUtil.fmtDateTime24(order.createdDate)}
            />
            <DisplayDetail
              label="Updated Date"
              value={OrderUtil.fmtDateTime24(order.updatedDate)}
            />
            <DisplayDetail
              label="Payment Method"
              value={order.paymentMethod || "-"}
            />
            <DisplayDetail
              label="Order Value"
              value={
                order.orderTotalAmount != null
                  ? OrderUtil.fmtMoney(order.orderTotalAmount, order.currency)
                  : ""
              }
            />
          </div>
        </div>


        {/* Dates */}
        {/* <ExpandableHeader headerLabel="Dates">
          <div className="px-4 py-3 mb-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
            <DisplayDetail label="Pickup From" value={fmtDateTime(pickupDate)} />
            <DisplayDetail label="Deliver By" value={fmtDateTime(deliveryDate)} />
          </div>
        </ExpandableHeader> */}
        <h3
          className="text-fluid-lg font-semibold mb-fluid-2"
        >
          Stops
        </h3>

        {/* Stops */}
        {(order?.stops ?? [])
          .slice() // don’t mutate original
          .sort((a, b) => (a.stopSequence ?? 0) - (b.stopSequence ?? 0))
          .map((s, idx) => {
            const typeLabel =
              s.stopType === "PICKUP"
                ? "Pick-Up"
                : s.stopType === "DROP_OFF" || s.stopType === "DROPOFF"
                ? "Drop-Off"
                : s.stopType || "Stop";

            const title = `Stop ${idx + 1}: ${typeLabel} – ${
              s.stopName || "—"
            }`;

            return (
              <ExpandableHeader key={s.id ?? idx} 
              headerLabel={title} 
              initiallyExpanded={false} 
              headerType="primary"
              icon={MapPin}>
                <div className="px-4 py-3 mb-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 dark:bg-acs-dark-surface text-gray-800 dark:text-acs-dark-text">
                  <DisplayDetail label="Location Name" value={s.stopName} />
                  <DisplayDetail
                    label="Warehouse Name"
                    value={s.warehouseName}
                  />
                  <DisplayDetail label="Address 1" value={s.addressLine1} />
                  <DisplayDetail label="Address 2" value={s.addressLine2} />
                  <DisplayDetail label="City" value={s.city} />
                  <DisplayDetail label="State" value={s.state} />
                  <DisplayDetail label="Country" value={s.country} />
                  <DisplayDetail label="Postal" value={s.zipCode} />

                  {/* Dates (optional per stop) */}
                  <DisplayDetail
                    label="Scheduled Gate-In"
                    value={OrderUtil.fmtDateTime24(s.scheduledGateIn)}
                  />
                  <DisplayDetail
                    label="Scheduled Gate-Out"
                    value={OrderUtil.fmtDateTime24(s.scheduledGateOut)}
                  />
                  {/* Add these only if you store them */}
                  {/* <DisplayDetail label="Actual Gate-In"  value={fmtDateTime(s.actualGateIn)} /> */}
                  {/* <DisplayDetail label="Actual Gate-Out" value={fmtDateTime(s.actualGateOut)} /> */}
                </div>
              </ExpandableHeader>
            );
          })}

         <h3
          className="text-fluid-lg font-semibold mb-fluid-2"
        >
          Total
        </h3>

        {/* Totals */}
        <ExpandableHeader headerLabel="Totals"  icon={DollarSign} initiallyExpanded={false}>
          <div className="px-4 py-3 mb-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 dark:bg-acs-dark-surface
                        text-gray-800 dark:text-acs-dark-text">
            <DisplayDetail
              label="Total Weight (LB)"
              value={
                totalWeight != null ? Number(totalWeight).toLocaleString() : ""
              }
            />
            <DisplayDetail
              label="Total Pallets"
              value={
                totalPallets != null
                  ? Number(totalPallets).toLocaleString()
                  : ""
              }
            />
          </div>
        </ExpandableHeader>

          {/* Line Items (read-only) */}
          <h3 className="text-fluid-lg font-semibold mb-fluid-2">Items</h3>

          {items.map((item, index) => {
            const itemEntries = [
              { label: "Line #", value: item.lineNumber },
              { label: "SKU", value: item.sku },
              { label: "Description", value: item.itemDescription },
              { label: "Item Quantity", value: item.itemQuantity },
              { label: "Quantity UOM", value: item.quantityUom },
              {
                label: "Weight Total",
                value:
                  item.weightTotal != null
                    ? `${Number(item.weightTotal).toLocaleString()} ${
                        item.weightUom || "LB"
                      }`
                    : "—",
              },
              { label: "Weight Qualifier", value: item.weightQualifier },
              {
                label: "Volume Total",
                value:
                  item.volumeTotal != null
                    ? `${Number(item.volumeTotal).toLocaleString()} ${
                        item.volumeUom || ""
                      }`
                    : "—",
              },
            ];

            return (
              <ExpandableHeader
                key={item.id ?? index}
                headerSection={`Item #${index + 1}`}
                headerLabel={item.sku || `Item ${index + 1}`}
                icon={Package}
                headerType="success"
                initiallyExpanded={false}
              >
                  <div className="grid grid-cols-5 gap-x-fluid-2 gap-y-fluid-3 
                        p-fluid-3 
                        bg-[#E0F2F1] dark:bg-acs-dark-surface
                        text-gray-800 dark:text-acs-dark-text">
                  {itemEntries.map((entry, i) => (
                    <DisplayDetail
                      key={i}
                      label={entry.label}
                      value={entry.value || "—"}
                    />
                  ))}

                  {/* Quantities breakdown */}
                  {Array.isArray(item.quantities) && item.quantities.length > 0 && (
                    <>
                      <div className="col-span-full mt-fluid-2 border-t border-gray-300 pt-fluid-2">
                        <h4 className="text-fluid-base font-semibold mb-fluid-1">
                          Quantities
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-x-fluid-2 gap-y-fluid-3">
                          {item.quantities.map((q, qIdx) => (
                            <React.Fragment key={qIdx}>
                              <DisplayDetail
                                label="Container Type"
                                value={q.containerType || "—"}
                              />
                              <DisplayDetail
                                label="Quantity"
                                value={
                                  q.quantity != null
                                    ? Number(q.quantity).toLocaleString()
                                    : "—"
                                }
                              />
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </ExpandableHeader>
            );
          })}



        <h3
          className="text-fluid-lg font-semibold mb-fluid-2"
        >
          Party
        </h3>

         {/* Parties */}
        <ExpandableHeader headerLabel="Parties" icon={Users} initiallyExpanded={false}>
          <div className="px-4 py-3 mb-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 dark:bg-acs-dark-surface
                        text-gray-800 dark:text-acs-dark-text">
            <DisplayDetail
              label="Shipper"
              value={
                shipper?.name || "-"
              }
            />
            <DisplayDetail
              label="Receiver"
              value={
                receiver?.name || "-"
              }
            />
          </div>
        </ExpandableHeader>
       
      </div>
    </div>
    </div>
  );
};

export default OrderDetailsView;
